import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import './CatalogManager.css';

interface Column<T> {
  key: keyof T | string;
  label: string;
  render?: (item: T) => React.ReactNode;
}

interface CatalogManagerProps<T> {
  title: string;
  apiEndpoint: string;
  columns: Column<T>[];
  formFields: FormField[];
  getItemId: (item: T) => number;
  getItemAttributes: (item: T) => any;
  createEmptyItem: () => any;
  validateItem?: (item: any) => string | null;
}

interface FormField {
  name: string;
  label: string;
  type: 'text' | 'select' | 'email' | 'checkbox';
  required?: boolean;
  options?: { value: string; label: string }[];
}

function CatalogManager<T>({
  title,
  apiEndpoint,
  columns,
  formFields,
  getItemId,
  getItemAttributes,
  createEmptyItem,
  validateItem
}: CatalogManagerProps<T>) {
  const { t } = useTranslation();
  const [items, setItems] = useState<T[]>([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState<any>(null);
  const [formData, setFormData] = useState<any>(createEmptyItem());
  const [error, setError] = useState<string | null>(null);
  const [filterActive, setFilterActive] = useState<'all' | 'active' | 'inactive'>('active');

  const API_BASE_URL = process.env.REACT_APP_API_URL || sessionStorage.getItem('backendUrl') ||'http://localhost:1337';

  useEffect(() => {
    fetchItems();
  }, [filterActive]);

  const fetchItems = async () => {
    setLoading(true);
    setError(null);
    try {
      let url = `${API_BASE_URL}/api/${apiEndpoint}?pagination[pageSize]=1000&sort=id:desc`;

      if (filterActive === 'active') {
        url += '&filters[activo][$eq]=true';
      } else if (filterActive === 'inactive') {
        url += '&filters[activo][$eq]=false';
      }

      const response = await fetch(url);
      const data = await response.json();

      if (response.ok) {
        setItems(data.data);
      } else {
        setError(t('loadDataError'));
      }
    } catch (err) {
      setError(t('connectionError'));
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setEditingItem(null);
    setFormData(createEmptyItem());
    setShowModal(true);
  };

  const handleEdit = (item: T) => {
    setEditingItem(item);
    setFormData({ ...getItemAttributes(item) });
    setShowModal(true);
  };

  const handleDelete = async (item: T) => {
    if (!window.confirm(t('confirmDeleteItem'))) {
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/api/${apiEndpoint}/${getItemId(item)}`, {
        method: 'DELETE'
      });

      if (response.ok) {
        fetchItems();
      } else {
        alert(t('deleteItemError'));
      }
    } catch (err) {
      alert(t('connectionError'));
      console.error(err);
    }
  };

  const handleToggleActive = async (item: T) => {
    const attrs = getItemAttributes(item);
    try {
      const response = await fetch(`${API_BASE_URL}/api/${apiEndpoint}/${getItemId(item)}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          data: {
            ...attrs,
            activo: !attrs.activo
          }
        })
      });

      if (response.ok) {
        fetchItems();
      } else {
        alert(t('updateItemError'));
      }
    } catch (err) {
      alert(t('connectionError'));
      console.error(err);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validación personalizada
    if (validateItem) {
      const validationError = validateItem(formData);
      if (validationError) {
        alert(validationError);
        return;
      }
    }

    try {
      const url = editingItem
        ? `${API_BASE_URL}/api/${apiEndpoint}/${getItemId(editingItem)}`
        : `${API_BASE_URL}/api/${apiEndpoint}`;

      const method = editingItem ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ data: formData })
      });

      if (response.ok) {
        setShowModal(false);
        fetchItems();
      } else {
        const errorData = await response.json();
        alert(errorData?.error?.message || t('saveItemError'));
      }
    } catch (err) {
      alert(t('connectionError'));
      console.error(err);
    }
  };

  const handleInputChange = (field: string, value: any) => {
    setFormData({ ...formData, [field]: value });
  };

  return (
    <div className="catalog-manager">
      <div className="catalog-header">
        <h2>{title}</h2>
        <div className="catalog-actions">
          <div className="filter-buttons">
            <button
              className={filterActive === 'all' ? 'active' : ''}
              onClick={() => setFilterActive('all')}
            >
              {t('all')}
            </button>
            <button
              className={filterActive === 'active' ? 'active' : ''}
              onClick={() => setFilterActive('active')}
            >
              {t('actives')}
            </button>
            <button
              className={filterActive === 'inactive' ? 'active' : ''}
              onClick={() => setFilterActive('inactive')}
            >
              {t('inactives')}
            </button>
          </div>
          <button className="btn-primary" onClick={handleCreate}>
            {t('createNew')}
          </button>
        </div>
      </div>

      {error && <div className="error-message">{error}</div>}

      {loading ? (
        <div className="loading">{t('loading')}</div>
      ) : (
        <table className="catalog-table">
          <thead>
            <tr>
              {columns.map((col, idx) => (
                <th key={idx}>{col.label}</th>
              ))}
              <th>{t('state')}</th>
              <th>{t('actions')}</th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 ? (
              <tr>
                <td colSpan={columns.length + 2} style={{ textAlign: 'center' }}>
                  {t('noItems')}
                </td>
              </tr>
            ) : (
              items.map((item) => {
                const attrs = getItemAttributes(item);
                return (
                  <tr key={getItemId(item)} className={!attrs.activo ? 'inactive-row' : ''}>
                    {columns.map((col, idx) => (
                      <td key={idx}>
                        {col.render
                          ? col.render(item)
                          : String(attrs[col.key as string] || '')}
                      </td>
                    ))}
                    <td>
                      <span className={`status-badge ${attrs.activo ? 'active' : 'inactive'}`}>
                        {attrs.activo ? t('active') : t('inactive')}
                      </span>
                    </td>
                    <td className="actions-cell">
                      <button
                        className="btn-small btn-edit"
                        onClick={() => handleEdit(item)}
                        title={t('edit')}
                      >
                        ✏️
                      </button>
                      <button
                        className="btn-small btn-toggle"
                        onClick={() => handleToggleActive(item)}
                        title={attrs.activo ? t('deactivate') : t('activate')}
                      >
                        {attrs.activo ? '🔴' : '🟢'}
                      </button>
                      <button
                        className="btn-small btn-delete"
                        onClick={() => handleDelete(item)}
                        title={t('delete')}
                      >
                        🗑️
                      </button>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      )}

      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{editingItem ? t('editItem', { title }) : t('createItem', { title })}</h3>
              <button className="modal-close" onClick={() => setShowModal(false)}>
                ✕
              </button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="form-body">
                {formFields.map((field) => (
                  <div key={field.name} className="form-group">
                    <label>
                      {field.label}
                      {field.required && <span className="required">*</span>}
                    </label>
                    {field.type === 'select' ? (
                      <select
                        value={formData[field.name] || ''}
                        onChange={(e) => handleInputChange(field.name, e.target.value)}
                        required={field.required}
                      >
                        <option value="">{t('selectOption')}</option>
                        {field.options?.map((opt) => (
                          <option key={opt.value} value={opt.value}>
                            {opt.label}
                          </option>
                        ))}
                      </select>
                    ) : field.type === 'checkbox' ? (
                      <input
                        type="checkbox"
                        checked={formData[field.name] || false}
                        onChange={(e) => handleInputChange(field.name, e.target.checked)}
                      />
                    ) : (
                      <input
                        type={field.type}
                        value={formData[field.name] || ''}
                        onChange={(e) => handleInputChange(field.name, e.target.value)}
                        required={field.required}
                      />
                    )}
                  </div>
                ))}
              </div>
              <div className="modal-footer">
                <button type="button" className="btn-secondary" onClick={() => setShowModal(false)}>
                  {t('cancel')}
                </button>
                <button type="submit" className="btn-primary">
                  {editingItem ? t('saveChanges') : t('create')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default CatalogManager;
